import {
  Bell,
  Briefcase,
  Check,
  CircleHelp,
  Lock,
  Users,
  Video,
  X,
} from 'lucide-react';
import {
  appointmentColorName,
  appointmentColorOf,
} from 'constants/appointmentColors';
import useText from 'hooks/useText';

const RESPONSES = ['accepted', 'declined', 'tentative', 'needsAction'];
const UNITS = [
  ['weeks', 10080],
  ['days', 1440],
  ['hours', 60],
  ['minutes', 1],
];

const iconClass = 'flex shrink-0 text-gray-400';

const RESPONSE_ICONS = {
  accepted: <Check size={12} strokeWidth={3} className="text-green-600" />,
  declined: <X size={12} strokeWidth={3} className="text-red-600" />,
  tentative: (
    <CircleHelp size={12} strokeWidth={2.5} className="text-gray-600" />
  ),
};

// The largest unit that divides the minutes, as the form shows it: 1440 reads « 1 jour ».
const delayOf = (minutes) => {
  const [unit, size] = UNITS.find(
    ([, factor]) => minutes > 0 && minutes % factor === 0,
  ) ?? ['minutes', 1];
  return { amount: minutes / size, unit };
};

// The link comes from Google, where an outside organizer or a conference
// add-on sets it: anything but https (javascript:, data:…) is not a link.
const safeConferenceUrl = (url) => {
  try {
    return new URL(url).protocol === 'https:' ? url : '';
  } catch {
    return '';
  }
};

const Line = ({ icon, children }) => (
  <div className="flex items-start gap-2 text-sm text-gray-700">
    <span className={`${iconClass} mt-0.5`}>{icon}</span>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

/**
 * What Google Calendar keeps on an appointment, shown as Google does: the
 * link of its video call, its guests with their answers, its organizer when
 * the owner is only a guest, its reminders, its color, whether it makes its
 * owner busy and who sees it. Nothing for what is not set.
 */
const AppointmentGoogleDetails = ({ item }) => {
  const { text, plural } = useText('appointment');
  const {
    text: fieldText,
    plural: fieldPlural,
    language,
  } = useText('appointmentFields');
  // The Language manager keeps the current map only: French is its map.
  const languageCode =
    language?.current && language.current === language.languages?.get('fr')
      ? 'fr'
      : 'en';
  const attendees = item.attendees ?? [];
  const conferenceUrl = safeConferenceUrl(item.conference?.url ?? '');
  const color = appointmentColorOf(item.colorId);
  const reminders = item.reminders;
  const counts = RESPONSES.map((response) => [
    response,
    attendees.filter(
      (attendee) => (attendee.responseStatus || 'needsAction') === response,
    ).length,
  ]).filter(([, count]) => count > 0);
  const fieldLine = (label, value) =>
    text('fieldLine', { label: fieldText(label), value });
  const reminderLine = ({ method, minutes }) => {
    const { amount, unit } = delayOf(minutes);
    return [
      fieldText(`reminderMethod_${method}`),
      amount,
      fieldPlural(`reminderUnit_${unit}`, amount),
      fieldText('reminderBefore'),
    ].join(' ');
  };

  return (
    <>
      {conferenceUrl && (
        <Line icon={<Video size={16} />}>
          <a
            href={conferenceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            {text(
              item.conference.kind === 'meet' ? 'joinMeet' : 'joinConference',
            )}
          </a>
          <p className="mt-1 truncate text-xs text-gray-500">
            {conferenceUrl.replace(/^https?:\/\//, '')}
          </p>
        </Line>
      )}
      {attendees.length > 0 && (
        <Line icon={<Users size={16} />}>
          <p className="font-medium text-gray-900">
            {plural('guests', attendees.length)}
          </p>
          <p className="text-xs text-gray-500">
            {counts
              .map(([response, count]) => text(`count_${response}`, { count }))
              .join(', ')}
          </p>
          <ul
            aria-label={text('guestsLabel')}
            className="mt-2 flex flex-col gap-2"
          >
            {attendees.map((attendee, index) => {
              const response = attendee.responseStatus || 'needsAction';
              const name = attendee.displayName || attendee.email || '';
              return (
                <li
                  key={attendee.email || index}
                  className="flex items-center gap-2"
                >
                  <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold uppercase text-gray-600">
                    {name.charAt(0)}
                    {RESPONSE_ICONS[response] && (
                      <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white">
                        {RESPONSE_ICONS[response]}
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className="block truncate text-sm text-gray-900"
                      title={attendee.email}
                    >
                      {name}
                    </span>
                    {attendee.organizer && (
                      <span className="block text-xs text-gray-500">
                        {text('organizer')}
                      </span>
                    )}
                  </span>
                  <span className="sr-only">
                    {text(`response_${response}`)}
                  </span>
                </li>
              );
            })}
          </ul>
        </Line>
      )}
      {item.organizerEmail && (
        <Line icon={<Users size={16} />}>
          {text('organizedBy', { email: item.organizerEmail })}
        </Line>
      )}
      {reminders && (
        <Line icon={<Bell size={16} />}>
          {reminders.useDefault !== false || !reminders.overrides?.length ? (
            <p>{fieldText('remindersDefault')}</p>
          ) : (
            reminders.overrides.map((reminder, index) => (
              <p key={index}>{reminderLine(reminder)}</p>
            ))
          )}
        </Line>
      )}
      {color && (
        <Line
          icon={
            <span
              aria-hidden="true"
              className="block h-4 w-4 rounded-full"
              style={{ backgroundColor: color.hex }}
            />
          }
        >
          {fieldLine('colorLabel', appointmentColorName(color, languageCode))}
        </Line>
      )}
      {item.transparency && (
        <Line icon={<Briefcase size={16} />}>
          {fieldLine(
            'transparencyLabel',
            fieldText(`transparency_${item.transparency}`),
          )}
        </Line>
      )}
      {item.visibility && item.visibility !== 'default' && (
        <Line icon={<Lock size={16} />}>
          {fieldLine(
            'visibilityLabel',
            fieldText(`visibility_${item.visibility}`),
          )}
        </Line>
      )}
    </>
  );
};

export default AppointmentGoogleDetails;
